/**
 * Billing Service
 *
 * Service for managing subscriptions, billing, and payments.
 */

import { apiClient } from "./api";
import type {
  BillingStatus,
  Plan,
  CheckoutSession,
  PortalSession,
  Invoice,
  UpcomingInvoice,
  ProrationPreview,
} from "@/types/billing";

const BASE_PATH = "/api/v1/billing";

export const billingService = {
  /**
   * Get current billing status
   */
  async getStatus(): Promise<BillingStatus> {
    return apiClient.get<BillingStatus>(`${BASE_PATH}/status`);
  },

  /**
   * Get all available subscription plans
   */
  async getPlans(): Promise<Plan[]> {
    return apiClient.get<Plan[]>(`${BASE_PATH}/plans`);
  },

  /**
   * Create a Stripe Checkout session for subscribing to a tier
   */
  async createCheckout(tierName: string, successUrl: string, cancelUrl: string): Promise<CheckoutSession> {
    return apiClient.post<CheckoutSession>(`${BASE_PATH}/create-checkout`, {
      tier_name: tierName,
      success_url: successUrl,
      cancel_url: cancelUrl,
    });
  },

  /**
   * Change subscription to a different tier
   */
  async changeTier(tierName: string): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>(`${BASE_PATH}/change-tier`, {
      tier_name: tierName,
    });
  },

  /**
   * Cancel subscription (at end of current period)
   */
  async cancelSubscription(): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>(`${BASE_PATH}/cancel`);
  },

  /**
   * Reactivate a canceled subscription
   */
  async reactivateSubscription(): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>(`${BASE_PATH}/reactivate`);
  },

  /**
   * Get Stripe Customer Portal URL
   */
  async getPortalSession(): Promise<PortalSession> {
    return apiClient.post<PortalSession>(`${BASE_PATH}/portal`);
  },

  /**
   * Get invoice history
   */
  async getInvoices(): Promise<Invoice[]> {
    return apiClient.get<Invoice[]>(`${BASE_PATH}/invoices`);
  },

  /**
   * Get upcoming invoice preview
   */
  async getUpcomingInvoice(): Promise<UpcomingInvoice | null> {
    return apiClient.get<UpcomingInvoice | null>(`${BASE_PATH}/upcoming-invoice`);
  },

  /**
   * Preview proration cost before switching tiers
   */
  async previewTierChange(tierName: string): Promise<ProrationPreview> {
    return apiClient.post<ProrationPreview>(`${BASE_PATH}/preview-tier-change`, {
      tier_name: tierName,
    });
  },
};
